import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Lock, 
  Mail, 
  Phone, 
  Building2, 
  Calendar,
  MessageSquare,
  Users,
  RefreshCw,
  LogOut,
  Search
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_PASSWORD = 'CausalerDataLoader';

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Check if already authenticated in session
  useEffect(() => {
    const auth = sessionStorage.getItem('causaler_admin_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch submissions when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchSubmissions();
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('causaler_admin_auth', 'true');
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('causaler_admin_auth');
    setPassword('');
  };

  const fetchSubmissions = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/contact`);
      // Sort by date, newest first
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setSubmissions(sorted);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSubmissions = submissions.filter(sub => 
    sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (sub.company && sub.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
    sub.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="pt-20 min-h-screen bg-gradient-subtle" data-testid="admin-login-page">
        <div className="max-w-md mx-auto px-4 py-20">
          <Card className="rounded-3xl border-slate-100 shadow-card">
            <CardHeader className="text-center pb-4">
              <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-[#F5941E]" />
              </div>
              <CardTitle className="font-heading text-2xl">Admin Access</CardTitle>
              <p className="text-slate-600 text-sm mt-2">
                Enter password to view contact submissions
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin}>
                <div className="mb-4">
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    className="h-12 rounded-xl border-slate-200 focus:border-[#F5941E] focus:ring-[#F5941E]/20"
                    data-testid="admin-password-input"
                  />
                </div>
                {passwordError && (
                  <p className="text-red-500 text-sm mb-4" data-testid="admin-password-error">
                    {passwordError}
                  </p>
                )}
                <Button 
                  type="submit"
                  className="w-full bg-[#F5941E] hover:bg-[#D97F12] text-white rounded-full py-3 font-semibold"
                  data-testid="admin-login-button"
                >
                  Access Dashboard
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="pt-20 min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#0F172A]">
              Contact Submissions
            </h1>
            <p className="text-slate-600 mt-1">
              View and manage all contact form inquiries
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={fetchSubmissions}
              variant="outline"
              className="rounded-full border-slate-200 hover:border-[#1E5E9F]"
              disabled={isLoading}
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="rounded-full border-slate-200 hover:border-red-500 hover:text-red-500"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#F5941E]" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Inquiries</p>
                <p className="text-2xl font-bold font-heading">{submissions.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#1E5E9F]" />
              </div>
              <div>
                <p className="text-sm text-slate-500">This Month</p>
                <p className="text-2xl font-bold font-heading">
                  {submissions.filter(s => {
                    const date = new Date(s.created_at);
                    const now = new Date();
                    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">With Company</p>
                <p className="text-2xl font-bold font-heading">
                  {submissions.filter(s => s.company).length}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, company, or message..."
              className="pl-12 h-12 rounded-xl border-slate-200 focus:border-[#F5941E]"
              data-testid="admin-search-input"
            />
          </div>
        </div>

        {/* Submissions List */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[#F5941E] mx-auto mb-4" />
              <p className="text-slate-600">Loading submissions...</p>
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <Card className="rounded-2xl border-slate-100">
              <CardContent className="p-12 text-center">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">No submissions yet</h3>
                <p className="text-slate-600">
                  {searchTerm ? 'No results match your search.' : 'Contact form submissions will appear here.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredSubmissions.map((submission, index) => (
              <Card 
                key={submission.id} 
                className="rounded-2xl border-slate-100 hover:shadow-md transition-shadow"
                data-testid={`submission-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                          <span className="text-[#F5941E] font-semibold">
                            {submission.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-lg">{submission.name}</h3>
                          {submission.company && (
                            <p className="text-sm text-slate-500">{submission.company}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 mb-4 text-sm">
                        <a 
                          href={`mailto:${submission.email}`}
                          className="flex items-center gap-2 text-slate-600 hover:text-[#F5941E] transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          {submission.email}
                        </a>
                        {submission.phone && (
                          <a 
                            href={`tel:${submission.phone}`}
                            className="flex items-center gap-2 text-slate-600 hover:text-[#F5941E] transition-colors"
                          >
                            <Phone className="w-4 h-4" />
                            {submission.phone}
                          </a>
                        )}
                      </div>
                      
                      <div className="bg-slate-50 rounded-xl p-4">
                        <p className="text-slate-700 whitespace-pre-wrap">{submission.message}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-sm text-slate-500">
                        {formatDate(submission.created_at)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
